import * as React from 'react';
import { SxProps, Theme, Typography } from '@mui/material';
import { keygen, theme } from '../../utils';
import { Button } from '../Button';
import { Icon, IconKey } from '../Icon';
import { LayoutGrid } from '../LayoutGrid';

const styles: Record<string, SxProps<Theme>> = {
  label: {
    color: 'primary.main'
  },
  lastNode: {
    color: 'text.primary'
  },
  crumbsWrapper: {
    '&>button': {
      padding: 0,
      minWidth: '0',
      minHeight: '0',
      '&>span>svg': {
        height: 3,
        width: 3
      },
      '& p': {
        textTransform: 'none'
      },
      '& p.labelBody2': {
        fontSize: 'body2.fontSize',
        fontWeight: 'body2.fontWeight',
        lineHeight: 'initial',
        textAlign: 'left'
      },
      '& p.labelBody1': {
        fontSize: 'body1.fontSize',
        fontWeight: 'body1.fontWeight',
        lineHeight: 'initial',
        color: 'text.primary'
      }
    }
  },
  crumbChevron: {
    margin: 0,
    height: theme.spacing(2.5),
    width: theme.spacing(2.5),
    color: 'grey.900'
  }
};

interface IBreadcrumb {
  icon?: IconKey;
  labelName?: string;
  text?: string;
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

export interface BreadcrumbProps {
  items: IBreadcrumb[];
  sx?: SxProps<Theme>;
}

const Breadcrumb = ({ items, sx }: BreadcrumbProps) => {
  const { label, lastNode, crumbsWrapper, crumbChevron } = styles;

  return (
    <nav aria-label="breadcrumb">
      <LayoutGrid container direction="row" justify="flex-start" alignItems="center" sx={[crumbsWrapper, sx] as SxProps<Theme>}>
        {items.map((crumb, index, allCrumbs) => {
          return (
            <React.Fragment key={keygen()}>
              {!crumb.labelName && !crumb.text ? (
                <Button
                  disabled={allCrumbs.length > 1 && index === allCrumbs.length - 1}
                  onClick={crumb.onClick}
                  icon={crumb.icon}
                  label={crumb.icon}
                />
              ) : (
                <Button
                  disabled={allCrumbs.length > 1 && index === allCrumbs.length - 1}
                  onClick={crumb.onClick}
                  icon={crumb.icon}
                  iconPosition="left"
                  size="small"
                  label={crumb.icon}
                  aria-current={allCrumbs.length >= 2 && index === allCrumbs.length - 2}
                >
                  {crumb.labelName && (
                    <Typography
                      variant="body2"
                      className="labelBody2"
                      sx={allCrumbs.length > 1 && index === allCrumbs.length - 1 ? lastNode : label}
                      aria-label={crumb.labelName}
                    >
                      {crumb.labelName}
                    </Typography>
                  )}
                  {crumb.text && (
                    <Typography
                      variant="body1"
                      className="labelBody1"
                      sx={allCrumbs.length > 1 && index === allCrumbs.length - 1 ? lastNode : undefined}
                      aria-label={crumb.text}
                    >
                      {crumb.text}
                    </Typography>
                  )}
                </Button>
              )}
              {index !== allCrumbs.length - 1 ? <Icon icon="ChevronRight" fontSize="small" sx={crumbChevron} /> : null}
            </React.Fragment>
          );
        })}
      </LayoutGrid>
    </nav>
  );
};

Breadcrumb.displayName = 'BreadCrumb';

export { Breadcrumb };
